import React, { useState } from 'react';
import { CheckerSelector, CheckerValue } from '../CheckerSelector';
import { ActionSelector, ActionValue } from '../ActionSelector';
import { Button, Card } from '@scalingworks/react-admin-ui';
import {
  FaPlus,
  FaTrash,
  FaChevronUp,
  FaChevronDown,
  FaGripVertical,
  FaCopy,
  FaRulerCombined,
  FaInfoCircle,
} from 'react-icons/fa';
import { useStampingPreviews } from '~/resources/loyalty/stamp-template/hooks/useStampingPreviews';
import type {
  ActionSchema,
  CheckerSchema,
} from '~/resources/loyalty/stamp-template/hooks/useStampingSchemas';
import { STAMP_CONFIG } from '~/resources/loyalty/stamp-template/config/stamp-config';

export interface StampingRule {
  checkers: CheckerValue[];
  action: ActionValue;
  priority: number;
}

interface StampingRulesManagerProps {
  rules: StampingRule[];
  onChange: (rules: StampingRule[]) => void;
  availableCheckers: CheckerSchema[];
  availableActions: ActionSchema[];
  errors?: any;
  disabled?: boolean;
}

export const StampingRulesManager: React.FC<StampingRulesManagerProps> = ({
  rules = [],
  onChange,
  availableCheckers,
  availableActions,
  errors,
  disabled = false,
}) => {
  const [expandedRules, setExpandedRules] = useState<Set<number>>(new Set([0]));
  const [preview, setPreview] = useState<string>('');
  const { getRulePreview } = useStampingPreviews({ availableCheckers, availableActions });

  // Helper function to get checkers array safely
  const getCheckersArray = (rule: StampingRule): CheckerValue[] => {
    return Array.isArray(rule.checkers) ? rule.checkers : [];
  };

  // Helper function to create default checker
  const createDefaultChecker = (): CheckerValue => ({
    code: 'always-true',
    params: {},
  });

  // Helper function to create default action
  const createDefaultAction = (): ActionValue => ({
    code: 'manual-stamps',
    params: { stamps: 1 },
  });

  // Validate and fix rule structure on first render
  React.useEffect(() => {
    const needsFix = rules.some(
      (rule) => !Array.isArray(rule.checkers) || !rule.action || typeof rule.priority !== 'number'
    );

    if (needsFix) {
      const fixedRules = rules.map((rule, index) => ({
        ...rule,
        checkers: Array.isArray(rule.checkers)
          ? rule.checkers
          : [{ code: 'always-true', params: {} }],
        action: rule.action || { code: 'manual-stamps', params: { stamps: 1 } },
        priority: typeof rule.priority === 'number' ? rule.priority : index + 1,
      }));
      onChange(fixedRules);
    }
  }, []);

  // Initialize with at least one rule if empty
  React.useEffect(() => {
    if (rules.length === 0) {
      const defaultRule: StampingRule = {
        checkers: [{ code: 'always-true', params: {} }],
        action: { code: 'manual-stamps', params: { stamps: 1 } },
        priority: 1,
      };
      onChange([defaultRule]);
    }
  }, [rules.length, onChange]);

  // Add a new rule
  const handleAddRule = () => {
    const newRule: StampingRule = {
      checkers: [createDefaultChecker()],
      action: createDefaultAction(),
      priority: rules.length + 1,
    };
    onChange([...rules, newRule]);
    setExpandedRules(new Set([...expandedRules, rules.length]));
  };

  // Remove a rule
  const handleRemoveRule = (index: number) => {
    const updatedRules = rules.filter((_, i) => i !== index);
    // Update priorities
    const reprioritizedRules = updatedRules.map((rule, i) => ({
      ...rule,
      priority: i + 1,
    }));
    onChange(
      reprioritizedRules.length > 0
        ? reprioritizedRules
        : [
            {
              checkers: [createDefaultChecker()],
              action: createDefaultAction(),
              priority: 1,
            },
          ]
    );
  };

  // Duplicate a rule
  const handleDuplicateRule = (index: number) => {
    const ruleToDuplicate = rules[index];
    const newRule: StampingRule = {
      ...JSON.parse(JSON.stringify(ruleToDuplicate)), // Deep clone
      priority: rules.length + 1,
    };
    onChange([...rules, newRule]);
    setExpandedRules(new Set([...expandedRules, rules.length]));
  };

  // Move rule up
  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updatedRules = [...rules];
    [updatedRules[index - 1], updatedRules[index]] = [updatedRules[index], updatedRules[index - 1]];
    // Update priorities
    updatedRules[index - 1].priority = index;
    updatedRules[index].priority = index + 1;
    onChange(updatedRules);
  };

  // Move rule down
  const handleMoveDown = (index: number) => {
    if (index === rules.length - 1) return;
    const updatedRules = [...rules];
    [updatedRules[index], updatedRules[index + 1]] = [updatedRules[index + 1], updatedRules[index]];
    // Update priorities
    updatedRules[index].priority = index + 1;
    updatedRules[index + 1].priority = index + 2;
    onChange(updatedRules);
  };

  // Update a specific rule
  const handleUpdateRule = (index: number, updatedRule: Partial<StampingRule>) => {
    const updatedRules = [...rules];
    updatedRules[index] = { ...updatedRules[index], ...updatedRule };
    onChange(updatedRules);
  };

  // Add checker to a rule
  const handleAddChecker = (ruleIndex: number) => {
    const rule = rules[ruleIndex];
    const currentCheckers = getCheckersArray(rule);
    const newChecker = createDefaultChecker();
    handleUpdateRule(ruleIndex, { checkers: [...currentCheckers, newChecker] });
  };

  // Remove checker from a rule
  const handleRemoveChecker = (ruleIndex: number, checkerIndex: number) => {
    const rule = rules[ruleIndex];
    const currentCheckers = getCheckersArray(rule);
    const updatedCheckers = currentCheckers.filter((_, i) => i !== checkerIndex);
    // Ensure at least one checker remains
    if (updatedCheckers.length === 0) {
      updatedCheckers.push(createDefaultChecker());
    }
    handleUpdateRule(ruleIndex, { checkers: updatedCheckers });
  };

  // Update checker in a rule
  const handleUpdateChecker = (
    ruleIndex: number,
    checkerIndex: number,
    checker: CheckerValue,
    preview?: string
  ) => {
    const rule = rules[ruleIndex];
    const currentCheckers = getCheckersArray(rule);
    const updatedCheckers = [...currentCheckers];
    updatedCheckers[checkerIndex] = checker;

    handleUpdateRule(ruleIndex, { checkers: updatedCheckers });
    setPreview(preview || '');
  };

  // Toggle rule expansion
  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedRules);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRules(newExpanded);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaRulerCombined className="text-purple-600 text-lg" />
          <h3 className="text-sm font-semibold text-gray-700">Stamping Rules</h3>
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-1">
          <FaInfoCircle />
          <span>Rules are evaluated in priority order</span>
        </div>
      </div>

      {/* Rules List */}
      <div className="space-y-3">
        {rules?.map((rule, ruleIndex) => (
          <Card key={ruleIndex} className="border-2 hover:border-purple-300 transition-colors">
            {/* Rule Header */}
            <div
              className="flex items-center justify-between p-4 cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
              onClick={() => toggleExpanded(ruleIndex)}
            >
              <div className="flex items-center gap-3">
                <FaGripVertical className="text-gray-400" />
                <div>
                  <h4 className="font-semibold text-sm text-gray-800">
                    Rule {ruleIndex + 1} (Priority: {rule.priority})
                  </h4>
                  <p className="text-xs text-gray-600 mt-1">{getRulePreview(rule)}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Action Buttons */}
                {!disabled && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveUp(ruleIndex);
                      }}
                      disabled={ruleIndex === 0}
                      className="p-1.5 text-gray-600 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <FaChevronUp className="text-sm" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveDown(ruleIndex);
                      }}
                      disabled={ruleIndex === rules.length - 1}
                      className="p-1.5 text-gray-600 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <FaChevronDown className="text-sm" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicateRule(ruleIndex);
                      }}
                      className="p-1.5 text-gray-600 hover:text-green-600"
                      title="Duplicate rule"
                    >
                      <FaCopy className="text-sm" />
                    </button>
                    {rules.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveRule(ruleIndex);
                        }}
                        className="p-1.5 text-gray-600 hover:text-red-600"
                        title="Delete rule"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    )}
                  </>
                )}

                {/* Expand/Collapse */}
                <div className="text-gray-400">
                  {expandedRules.has(ruleIndex) ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </div>
            </div>

            {/* Rule Content (Expanded) */}
            {expandedRules.has(ruleIndex) && (
              <div className="p-4 space-y-4 border-t">
                {/* Conditions Section */}
                <div>
                  <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="text-indigo-600">IF</span> all conditions are met:
                  </h5>
                  <div className="space-y-3 pl-4">
                    {(Array.isArray(rule.checkers) ? rule.checkers : []).map(
                      (checker, checkerIndex) => (
                        <div key={checkerIndex} className="relative">
                          {/* Remove checker button */}
                          {(Array.isArray(rule.checkers) ? rule.checkers : []).length > 1 &&
                            !disabled && (
                              <button
                                onClick={() => handleRemoveChecker(ruleIndex, checkerIndex)}
                                className="absolute -top-2 -right-2 z-10 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors shadow-md"
                                title="Remove condition"
                              >
                                <FaTrash className="text-xs" />
                              </button>
                            )}

                          {checkerIndex > 0 && (
                            <div className="flex items-center justify-center mb-2">
                              <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                                AND
                              </span>
                            </div>
                          )}

                          <CheckerSelector
                            value={checker}
                            onChange={(updatedChecker, preview) =>
                              handleUpdateChecker(ruleIndex, checkerIndex, updatedChecker, preview)
                            }
                            availableCheckers={availableCheckers}
                            error={errors?.[ruleIndex]?.checkers?.[checkerIndex]}
                            label={`Condition ${checkerIndex + 1}`}
                            required={checkerIndex === 0}
                            disabled={disabled}
                          />
                        </div>
                      )
                    )}

                    {/* Add Condition Button */}
                    {!disabled &&
                      (Array.isArray(rule.checkers) ? rule.checkers : []).length <
                        STAMP_CONFIG.rules.maxCheckersPerRule && (
                        <div className="flex justify-center pt-2">
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleAddChecker(ruleIndex)}
                            className="flex items-center gap-2"
                          >
                            <FaPlus className="text-xs" />
                            Add Condition
                          </Button>
                        </div>
                      )}

                    {/* Condition limit reached message */}
                    {!disabled &&
                      STAMP_CONFIG.rules.showLimitMessage &&
                      (Array.isArray(rule.checkers) ? rule.checkers : []).length >=
                        STAMP_CONFIG.rules.maxCheckersPerRule && (
                        <div className="flex justify-center pt-2">
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <FaInfoCircle />
                            Maximum {STAMP_CONFIG.rules.maxCheckersPerRule} conditions per rule
                            reached
                          </p>
                        </div>
                      )}
                  </div>
                </div>

                {/* Action Section */}
                <div>
                  <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="text-yellow-600">THEN</span> award:
                  </h5>
                  <div className="pl-4">
                    <ActionSelector
                      value={rule.action}
                      onChange={(action) => handleUpdateRule(ruleIndex, { action })}
                      availableActions={availableActions}
                      checkers={rule.checkers}
                      error={errors?.[ruleIndex]?.action}
                      label="Stamp Action"
                      required
                      disabled={disabled}
                    />
                  </div>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Add Rule Button */}
      {!disabled && rules.length < STAMP_CONFIG.rules.maxStampingRules && (
        <div className="flex justify-center pt-2">
          <Button variant="default" onClick={handleAddRule} className="flex items-center gap-2">
            <FaPlus />
            Add New Rule
          </Button>
        </div>
      )}

      {/* Limit reached message */}
      {!disabled &&
        STAMP_CONFIG.rules.showLimitMessage &&
        rules.length >= STAMP_CONFIG.rules.maxStampingRules && (
          <div className="flex justify-center pt-2">
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <FaInfoCircle />
              Maximum {STAMP_CONFIG.rules.maxStampingRules} stamping rules reached
            </p>
          </div>
        )}

      {/* Summary */}
      {rules.length > 0 && (
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <p className="text-sm font-medium text-purple-800 mb-2">How it works:</p>
          <ol className="text-xs text-purple-700 space-y-1 list-decimal list-inside">
            <li>Rules are evaluated in priority order (1, 2, 3...)</li>
            <li>The first rule where all conditions match will be applied</li>
            <li>Only one rule is applied per stamping event</li>
            <li>Make sure to order rules from most specific to least specific</li>
            <li>
              Please also includes how the customer can earn the stamp in detail in the T&C section
            </li>
          </ol>
        </div>
      )}

      {/* Error Message */}
      {errors && typeof errors === 'string' && (
        <div className="p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-xs text-red-600 flex items-center gap-1">
            <span>⚠️</span>
            {errors}
          </p>
        </div>
      )}
    </div>
  );
};

export default StampingRulesManager;
